import { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  company_name: "",
  phone: "",
  email: "",
  product_type: "Folding Umbrellas",
  quantity: "",
  message: "",
};

const InquiryForm = () => {
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = (fieldName, fieldValue, fullForm) => {
    const value = typeof fieldValue === "string" ? fieldValue.trim() : fieldValue;

    if (fieldName === "name") {
      if (!value) return "Name is required.";
      if (value.length < 2) return "Name should be at least 2 characters.";
    }

    if (fieldName === "company_name") {
      if (!value) return "Company name is required.";
      if (value.length < 2) return "Company name should be at least 2 characters.";
    }

    if (fieldName === "phone") {
      if (!value) return "Phone number is required.";
      if (!/^\d{8,15}$/.test(String(value))) return "Enter a valid phone number (8-15 digits).";
    }

    if (fieldName === "email") {
      if (!value) return "Email is required.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Enter a valid email address.";
    }

    if (fieldName === "product_type") {
      if (!value) return "Please select a product type.";
    }

    if (fieldName === "quantity") {
      if (!value) return "Quantity is required.";
      if (Number(value) <= 0) return "Quantity must be greater than 0.";
    }

    if (fieldName === "message") {
      if (!value) return "Message is required.";
      if (value.length < 3) return "Message should be at least 3 characters.";
    }

    return "";
  };

  const validateForm = (values) => {
    const fieldList = ["name", "company_name", "phone", "email", "product_type", "quantity", "message"];
    const nextErrors = {};

    fieldList.forEach((field) => {
      nextErrors[field] = validateField(field, values[field], values);
    });

    return nextErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const updatedForm = { ...formData, [name]: value };
    setFormData(updatedForm);

    if (touched[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value, updatedForm),
      }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value, formData),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateForm(formData);
    setErrors(nextErrors);
    setTouched({
      name: true,
      company_name: true,
      phone: true,
      email: true,
      product_type: true,
      quantity: true,
      message: true,
    });

    if (Object.values(nextErrors).some((errorMsg) => errorMsg)) {
      toast.error("Please correct the highlighted fields.");
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(`${API_URL}/inquiries`, {
        ...formData,
        quantity: Number(formData.quantity),
      });
      toast.success("Thank you. Our team will contact you with a corporate quote.");
      setFormData(initialForm);
      setErrors({});
      setTouched({});
    } catch (error) {
      const apiError = error?.response?.data?.detail;
      if (Array.isArray(apiError) && apiError[0]?.msg) {
        toast.error(apiError[0].msg);
      } else if (typeof apiError === "string") {
        toast.error(apiError);
      } else {
        toast.error("Submission failed. Please retry.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      onSubmit={handleSubmit}
      noValidate
      data-testid="contact-inquiry-form"
    >
      <div className="grid gap-4 md:grid-cols-2" data-testid="contact-inquiry-grid-fields">
        <div data-testid="contact-field-name-wrapper">
          <Input
            name="name"
            placeholder="Name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            data-testid="contact-input-name"
          />
          {touched.name && errors.name ? (
            <p className="mt-1 text-xs text-red-600" data-testid="contact-error-name">
              {errors.name}
            </p>
          ) : null}
        </div>

        <div data-testid="contact-field-company-wrapper">
          <Input
            name="company_name"
            placeholder="Company Name"
            value={formData.company_name}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            data-testid="contact-input-company-name"
          />
          {touched.company_name && errors.company_name ? (
            <p className="mt-1 text-xs text-red-600" data-testid="contact-error-company-name">
              {errors.company_name}
            </p>
          ) : null}
        </div>

        <div data-testid="contact-field-phone-wrapper">
          <Input
            name="phone"
            placeholder="Phone"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            data-testid="contact-input-phone"
          />
          {touched.phone && errors.phone ? (
            <p className="mt-1 text-xs text-red-600" data-testid="contact-error-phone">
              {errors.phone}
            </p>
          ) : null}
        </div>

        <div data-testid="contact-field-email-wrapper">
          <Input
            name="email"
            placeholder="Email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            data-testid="contact-input-email"
          />
          {touched.email && errors.email ? (
            <p className="mt-1 text-xs text-red-600" data-testid="contact-error-email">
              {errors.email}
            </p>
          ) : null}
        </div>

        <div data-testid="contact-field-product-wrapper">
          <select
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm text-slate-700 shadow-sm"
            name="product_type"
            value={formData.product_type}
            onChange={handleChange}
            onBlur={handleBlur}
            data-testid="contact-select-product-type"
          >
            <option value="Folding Umbrellas">Folding Umbrellas</option>
            <option value="Long Umbrellas">Long Umbrellas</option>
            <option value="Golf Umbrellas">Golf Umbrellas</option>
            <option value="Garden Umbrellas">Garden Umbrellas</option>
            <option value="Kids Umbrellas">Kids Umbrellas</option>
            <option value="Custom Logo Umbrellas">Custom Logo Umbrellas</option>
            <option value="Gazebos">Gazebos</option>
            <option value="Promotional Kiosks">Promotional Kiosks</option>
          </select>
          {touched.product_type && errors.product_type ? (
            <p className="mt-1 text-xs text-red-600" data-testid="contact-error-product-type">
              {errors.product_type}
            </p>
          ) : null}
        </div>

        <div data-testid="contact-field-quantity-wrapper">
          <Input
            name="quantity"
            placeholder="Quantity"
            type="number"
            min="1"
            value={formData.quantity}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            data-testid="contact-input-quantity"
          />
          {touched.quantity && errors.quantity ? (
            <p className="mt-1 text-xs text-red-600" data-testid="contact-error-quantity">
              {errors.quantity}
            </p>
          ) : null}
        </div>
      </div>

      <div data-testid="contact-field-message-wrapper">
        <Textarea
          name="message"
          placeholder="Message"
          value={formData.message}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          className="min-h-32"
          data-testid="contact-input-message"
        />
        {touched.message && errors.message ? (
          <p className="mt-1 text-xs text-red-600" data-testid="contact-error-message">
            {errors.message}
          </p>
        ) : null}
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-full bg-orange-500 py-5 font-semibold text-white hover:bg-orange-600"
        data-testid="contact-submit-button"
      >
        {isSubmitting ? "Submitting Inquiry..." : "Submit Corporate Inquiry"}
      </Button>
    </form>
  );
};

export default InquiryForm;